/**
 * Editing the stored payload of a BOARD template (a board archive: ``payload.archive``). Pure
 * functions over plain JSON: everything the form does not show (appearance, labels, blocks, ...)
 * is carried through untouched, and every reference the archive keeps stays consistent.
 */
type Json = Record<string, unknown>;

export interface TemplateColumn {
  ref: string;
  name: string;
  /** Tasks sitting in it (they go with it when it is removed). */
  tasks: number;
}

export interface TemplateTask {
  ref: string;
  column: string;
  title: string;
}

export interface BoardTemplateForm {
  boardName: string;
  description: string;
  columns: TemplateColumn[];
  tasks: TemplateTask[];
  includeTasks: boolean;
}

export const BOARD_NAME_MAX = 200;
export const DESCRIPTION_MAX = 5000;
export const COLUMN_NAME_MAX = 100;
export const TASK_TITLE_MAX = 500;

const list = (value: unknown): Json[] => (Array.isArray(value) ? (value as Json[]) : []);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');

export function readBoardTemplate(payload: Json): BoardTemplateForm {
  const archive = (payload['archive'] ?? {}) as Json;
  const board = (archive['board'] ?? {}) as Json;
  const cards = list(archive['cards']);
  return {
    boardName: str(board['name']),
    description: str(board['description']),
    columns: list(archive['columns']).map((c) => ({
      ref: str(c['ref']),
      name: str(c['name']),
      tasks: cards.filter((card) => card['column'] === c['ref']).length,
    })),
    tasks: cards.map((c) => ({
      ref: str(c['ref']),
      column: str(c['column']),
      title: str(c['title']),
    })),
    includeTasks: payload['include_tasks'] === true,
  };
}

/** A reference not used by anything in the archive yet (``[A-Za-z0-9_-]{1,40}``). */
export function freshRef(prefix: string, used: Iterable<string>): string {
  const taken = new Set(used);
  for (let n = 1; ; n += 1) {
    const ref = `${prefix}${String(n)}`;
    if (!taken.has(ref)) return ref;
  }
}

/** Validation the form shows before saving (the server checks the same and more). */
export function boardTemplateProblems(form: BoardTemplateForm): string[] {
  const problems: string[] = [];
  if (form.boardName.trim() === '') problems.push('boardName');
  if (form.columns.length === 0) problems.push('noColumns');
  if (form.columns.some((c) => c.name.trim() === '')) problems.push('columnName');
  const names = form.columns.map((c) => c.name.trim().toLowerCase());
  if (new Set(names).size !== names.length) problems.push('columnDuplicate');
  if (form.tasks.some((task) => task.title.trim() === '')) problems.push('taskTitle');
  return problems;
}

/**
 * The payload with the form applied. Columns come out in the form's order; a column that is gone
 * takes its tasks with it (and the recurring series whose source task went); the default column
 * is cleared when it is gone.
 */
export function applyBoardTemplate(payload: Json, form: BoardTemplateForm): Json {
  const archive = (payload['archive'] ?? {}) as Json;
  const board = (archive['board'] ?? {}) as Json;
  const oldColumns = new Map(list(archive['columns']).map((c) => [str(c['ref']), c]));
  const oldCards = new Map(list(archive['cards']).map((c) => [str(c['ref']), c]));
  const columns = form.columns.map((c) => {
    const stored = oldColumns.get(c.ref);
    return stored
      ? { ...stored, name: c.name.trim() }
      : { ref: c.ref, name: c.name.trim(), appearance_mode: 'header' };
  });
  const kept = new Set(columns.map((c) => str(c['ref'])));
  const cards = form.includeTasks
    ? form.tasks
        .filter((task) => kept.has(task.column))
        .map((task) => {
          const stored = oldCards.get(task.ref);
          return stored
            ? { ...stored, title: task.title.trim(), column: task.column }
            : { ref: task.ref, column: task.column, title: task.title.trim() };
        })
    : [];
  const cardRefs = new Set(cards.map((c) => str(c['ref'])));
  const rules = list(archive['rules']).filter(
    (r) =>
      cardRefs.has(str(r['source_card'])) && (r['column'] == null || kept.has(str(r['column']))),
  );
  const ruleRefs = new Set(rules.map((r) => str(r['ref'])));
  const cleanedCards = cards.map((c) => {
    const recurrence = (c as Json)['recurrence'] as Json | null | undefined;
    return recurrence && !ruleRefs.has(str(recurrence['rule'])) ? { ...c, recurrence: null } : c;
  });
  const description = form.description.trim();
  return {
    ...payload,
    archive: {
      ...archive,
      board: {
        ...board,
        name: form.boardName.trim(),
        description: description === '' ? null : description,
        default_column:
          board['default_column'] != null && kept.has(str(board['default_column']))
            ? board['default_column']
            : null,
      },
      columns,
      cards: cleanedCards,
      rules,
      counts: {
        ...((archive['counts'] ?? {}) as Json),
        columns: columns.length,
        cards: cleanedCards.length,
        recurrence_rules: rules.length,
      },
    },
  };
}
